<div class="getting-started-page">

  <div class="header-controls">
    <h3 class="page-heading">
      <fa-icon [icon]="pageHeaderIcon" class="page-header-icon" />
      Getting Started
    </h3>
    <button type="button" class="btn btn-print" (click)="print()">
      Print
    </button>
  </div>

  <div class="checklist-card">
    <div class="card-section-bar">Checklist</div>

    <div class="checklist-card-body">
      <div class="section-header">Getting Started</div>

      <div class="checklist-columns-header">
        <div class="col-todo-header">To Do</div>
        <div class="col-links-header">Where to look...</div>
      </div>

      <div class="checklist-item">
        <div class="col-todo">
          <div class="item-title-row">
            <button
              type="button"
              class="check-btn"
              [class.checked]="isChecked(0)"
              (click)="toggleItem(0)"
              aria-label="Toggle Enrollment checklist item"
            >
              @if (isChecked(0)) {
                <fa-icon [icon]="checkIcon" class="check-icon checked-icon" />
              } @else {
                <fa-icon [icon]="uncheckedIcon" class="check-icon unchecked-icon" />
              }
            </button>
            <span class="item-number-title">1. Enrollment</span>
          </div>
          <hr class="item-divider" />
          <div class="item-body">
            <p>Enroll students under Enroll and then Students List in the navigation menu.</p>
            <div class="checklist-item-body-content">
              <p>
                If your students are accessing WordFlight through Clever, select "Add Clever Section" to
                view the available Clever sections. Then select your Clever section and click "Add Sections."
                Students should then be able to access WordFlight through Clever.
              </p>
              <p>
                If your students are accessing WordFlight through ClassLink, select "Add ClassLink Section"
                to view the available ClassLink sections. Select your ClassLink section and then click
                "Roster Classes." Students should then be able to access WordFlight through ClassLink.
              </p>
              <p>
                If your students are logging in manually to WordFlight, select "Add" and fill in student
                information for each student. Students should then be able to access WordFlight at
                <a href="https://student.wordflight.com/login" target="_blank" class="inline-link">
                  https://student.wordflight.com/login
                </a>
                using the username and password you created.
              </p>
            </div>
          </div>
        </div>
        <div class="col-links">
          <div class="where-to-look-category">Enroll:</div>
          <a [routerLink]="'/students'" class="where-to-look-link">Student List</a>
        </div>
      </div>

      <div class="checklist-item">
        <div class="col-todo">
          <div class="item-title-row">
            <button
              type="button"
              class="check-btn"
              [class.checked]="isChecked(1)"
              (click)="toggleItem(1)"
              aria-label="Toggle WordFlight Screener Video checklist item"
            >
              @if (isChecked(1)) {
                <fa-icon [icon]="checkIcon" class="check-icon checked-icon" />
              } @else {
                <fa-icon [icon]="uncheckedIcon" class="check-icon unchecked-icon" />
              }
            </button>
            <span class="item-number-title">2. WordFlight Screener Video for Students</span>
          </div>
          <hr class="item-divider" />
          <div class="item-body">
            <p>Show students the "Introduction to the WordFlight Screener" video.</p>
          </div>
        </div>
        <div class="col-links">
          <div class="where-to-look-category">Knowledge Base: Videos</div>
          <a [routerLink]="'/resources/videos'" class="where-to-look-link">Welcome to the<br />WordFlight Screener</a>
        </div>
      </div>

      <div class="checklist-item">
        <div class="col-todo">
          <div class="item-title-row">
            <button
              type="button"
              class="check-btn"
              [class.checked]="isChecked(2)"
              (click)="toggleItem(2)"
              aria-label="Toggle Student Screener checklist item"
            >
              @if (isChecked(2)) {
                <fa-icon [icon]="checkIcon" class="check-icon checked-icon" />
              } @else {
                <fa-icon [icon]="uncheckedIcon" class="check-icon unchecked-icon" />
              }
            </button>
            <span class="item-number-title">3. Student Screener</span>
          </div>
          <hr class="item-divider" />
          <div class="item-body">
            <p>Have students log in and complete the Screener.</p>
            <div class="checklist-item-body-content">
              <p>
                The WordFlight Screener takes approximately 15-20 minutes. If a student takes longer than
                20 minutes to complete the Screener, it will save their work and log them out. They can log
                back in the next day to complete the remainder of the Screener.
              </p>
              <p>You will receive a notification when a student completes the Screener.</p>
            </div>
          </div>
        </div>
        <div class="col-links">
          <div class="where-to-look-category">Notifications</div>
          <a [routerLink]="'/notifications/recent'" class="where-to-look-link">Recent</a>
        </div>
      </div>

      <div class="checklist-item">
        <div class="col-todo">
          <div class="item-title-row">
            <button
              type="button"
              class="check-btn"
              [class.checked]="isChecked(3)"
              (click)="toggleItem(3)"
              aria-label="Toggle Screener Results checklist item"
            >
              @if (isChecked(3)) {
                <fa-icon [icon]="checkIcon" class="check-icon checked-icon" />
              } @else {
                <fa-icon [icon]="uncheckedIcon" class="check-icon unchecked-icon" />
              }
            </button>
            <span class="item-number-title">4. Screener Results</span>
          </div>
          <hr class="item-divider" />
          <div class="item-body">
            <p>
              View results for the WordFlight Screener by selecting the
              <a [routerLink]="'/reports/screener'" class="inline-link">"Screener Results"</a>
              report in the navigation menu. Decide which students to move into the WordFlight System
              by reviewing the Screener data.
            </p>
            <div class="checklist-item-body-content">
              <p>
                We recommend students who score in the "high risk" red category and "some risk" yellow
                category as excellent candidates for WordFlight.
              </p>
              <p>
                If a student scores less than 50 on the Screener and does not have sound-letter
                correspondence, we would recommend they do not use WordFlight yet.
              </p>
              <p>
                Borderline proficient students (400-450 for second-fourth graders or 300-350 for fifth
                graders and above) may be candidates for WordFlight. We recommend you consider outside
                assessments (benchmark data) to determine whether you think they would benefit from extra
                practice toward building automatic word recognition.
              </p>
            </div>
          </div>
        </div>
        <div class="col-links">
          <div class="where-to-look-category">Reports: Assessments</div>
          <a [routerLink]="'/reports/screener'" class="where-to-look-link">Screener Results</a>
        </div>
      </div>
    </div>
  </div>
</div>
